import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDrawPolygon,
  faFloppyDisk,
  faTrash,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

import {
  buildGridLayoutFromTiles,
  createMaskFromPoints,
  fetchScanReport,
  MASK_POINT_COUNT,
  masksEqual,
  polygonSelfIntersects,
  pointsToSvgPolygon,
  screenPointToNormalized,
} from './gridScan';
import camMap from './mapLogic';
import { updatePath } from './actions';
import { displayErrorModal } from '../../helpers/swal';
import RoundButton from '../../components/RoundButton';

import style from './style.module.scss';

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
const MODE_PAN = 'pan';
const MODE_MASK = 'mask';

const GridImageViewer = ({ region }) => {
  const dispatch = useDispatch();
  const containerRef = useRef();
  const mosaicRef = useRef();
  const dragRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [tiles, setTiles] = useState([]);
  const [reportState, setReportState] = useState('idle');
  const [mode, setMode] = useState(MODE_PAN);
  const [masks, setMasks] = useState(region?.masks || []);
  const [draftPoints, setDraftPoints] = useState([]);
  const [cursorPoint, setCursorPoint] = useState(null);
  const [selectedMaskId, setSelectedMaskId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadTiles = async () => {
      if (region?.reportUrl) {
        setReportState('loading');
        try {
          const report = await fetchScanReport(region.reportUrl);
          if (cancelled) return;
          setTiles(report.result?.tiles || []);
          setReportState('ready');
        } catch {
          if (cancelled) return;
          setTiles([]);
          setReportState('error');
        }
        return;
      }

      setTiles(region?.tiles || []);
      setReportState('ready');
    };

    loadTiles();

    return () => {
      cancelled = true;
    };
  }, [region?.regionId, region?.reportUrl, region?.tiles]);

  useEffect(() => {
    setMasks(region?.masks || []);
    setDraftPoints([]);
    setCursorPoint(null);
    setSelectedMaskId(null);
    setMode(MODE_PAN);
  }, [region?.regionId, region?.masks]);

  const grid = useMemo(
    () => buildGridLayoutFromTiles(tiles),
    [tiles],
  );

  const isDirty = !masksEqual(masks, region?.masks || []);
  const canMask = reportState === 'ready' && Boolean(grid);

  useEffect(() => {
    setView({ x: 0, y: 0, scale: 1 });
  }, [region?.regionId, region?.reportUrl, tiles]);

  const handleMouseDown = useCallback((event) => {
    if (mode !== MODE_PAN || event.button !== 0) return;
    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };
  }, [mode, view.x, view.y]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragRef.current) return;
      const {
        startX,
        startY,
        originX,
        originY,
      } = dragRef.current;
      setView((current) => ({
        ...current,
        x: originX + event.clientX - startX,
        y: originY + event.clientY - startY,
      }));
    };

    const handleMouseUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      const factor = event.deltaY > 0 ? 0.9 : 1.1;
      setView((current) => ({
        ...current,
        scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, current.scale * factor)),
      }));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const deleteSelectedMask = useCallback(() => {
    if (!selectedMaskId) return;
    setMasks((current) => current.filter((mask) => mask.id !== selectedMaskId));
    setSelectedMaskId(null);
  }, [selectedMaskId]);

  useEffect(() => {
    if (!canMask) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (!selectedMaskId) return;
      event.preventDefault();
      deleteSelectedMask();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canMask, selectedMaskId, deleteSelectedMask]);

  const startMaskMode = () => {
    setMode(MODE_MASK);
    setSelectedMaskId(null);
    setDraftPoints([]);
    setCursorPoint(null);
  };

  const handleMosaicMouseMove = useCallback((event) => {
    if (mode !== MODE_MASK) return;
    const point = screenPointToNormalized(event.clientX, event.clientY, mosaicRef.current);
    setCursorPoint(point);
  }, [mode]);

  const handleMosaicMouseLeave = useCallback(() => {
    setCursorPoint(null);
  }, []);

  const handleMosaicClick = useCallback((event) => {
    if (mode !== MODE_MASK || event.button !== 0) return;
    event.stopPropagation();

    const point = screenPointToNormalized(event.clientX, event.clientY, mosaicRef.current);
    if (!point) return;

    const next = [...draftPoints, point];
    if (polygonSelfIntersects(next)) return;

    setSelectedMaskId(null);
    if (next.length < MASK_POINT_COUNT) {
      setDraftPoints(next);
      return;
    }

    setMasks((existing) => [...existing, createMaskFromPoints(next)]);
    setDraftPoints([]);
    setMode(MODE_PAN);
    setCursorPoint(null);
  }, [mode, draftPoints]);

  const selectMask = useCallback((event, maskId) => {
    if (mode === MODE_MASK) return;
    event.stopPropagation();
    setSelectedMaskId(maskId);
    setDraftPoints([]);
    setMode(MODE_PAN);
  }, [mode]);

  const stopMaskMouseDown = useCallback((event) => {
    // Keep pan from starting when selecting a mask.
    event.stopPropagation();
  }, []);

  const deleteMaskById = useCallback((maskId) => {
    setMasks((current) => current.filter((item) => item.id !== maskId));
    setSelectedMaskId((current) => (current === maskId ? null : current));
  }, []);

  const handleMaskDoubleClick = useCallback((event, maskId) => {
    event.stopPropagation();
    deleteMaskById(maskId);
  }, [deleteMaskById]);

  const saveMasks = async () => {
    if (!region?.regionId || saving) return;
    setSaving(true);
    try {
      const result = await camMap.updateScanRegion(region.regionId, {
        ...region,
        masks,
      });
      if (result) {
        dispatch(updatePath('grid.regions', camMap.getScanRegionsForUi()));
        return;
      }
      displayErrorModal({ text: 'Failed to save masks to the mapping file.' });
    } finally {
      setSaving(false);
    }
  };

  const exitGridView = () => {
    dispatch(updatePath('grid.viewingRegionId', null));
  };

  const maskTitle = mode === MODE_MASK
    ? `Click ${MASK_POINT_COUNT} points (${draftPoints.length}/${MASK_POINT_COUNT})`
    : 'Add mask';

  const renderContent = () => {
    if (reportState === 'loading') {
      return (
        <div className={style.gridViewPlaceholder}>
          <p>Loading scan report…</p>
        </div>
      );
    }

    if (reportState === 'error') {
      return (
        <div className={style.gridViewPlaceholder}>
          <p>Failed to load scan report.</p>
        </div>
      );
    }

    if (!region?.reportUrl && !region?.tiles?.length) {
      return (
        <div className={style.gridViewPlaceholder}>
          <p>Grid preview</p>
          <p>
            {`(${region.startPan}, ${region.startTilt}) → (${region.endPan}, ${region.endTilt})`}
          </p>
          <p>Run grid creation to generate scan images.</p>
        </div>
      );
    }

    if (!tiles.length || !grid) {
      return (
        <div className={style.gridViewPlaceholder}>
          <p>No scan images found.</p>
        </div>
      );
    }

    const tileMap = new Map(
      grid.tiles.map((tile) => [`${tile.row}-${tile.col}`, tile]),
    );

    const draftPreview = cursorPoint && draftPoints.length > 0
      ? [...draftPoints, cursorPoint]
      : draftPoints;
    const previewInvalid = Boolean(
      cursorPoint
      && draftPoints.length === MASK_POINT_COUNT - 1
      && polygonSelfIntersects([...draftPoints, cursorPoint]),
    );
    const draftLineClass = previewInvalid
      ? style.gridMaskDraftLineInvalid
      : style.gridMaskDraftLine;
    // viewBox is 0..1 with preserveAspectRatio=none — compensate so handles stay circular.
    const mosaicRect = mosaicRef.current?.getBoundingClientRect();
    const pointRadiusPx = 4;
    const pointRx = mosaicRect?.width ? pointRadiusPx / mosaicRect.width : 0.006;
    const pointRy = mosaicRect?.height ? pointRadiusPx / mosaicRect.height : 0.006;

    return (
      // Pan/mask canvas — mouse-driven; keyboard delete handled globally.
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
         jsx-a11y/no-static-element-interactions */
      <div
        ref={mosaicRef}
        className={style.gridViewMosaicWrap}
        onClick={handleMosaicClick}
        onMouseMove={handleMosaicMouseMove}
        onMouseLeave={handleMosaicMouseLeave}
      >
        <div
          className={style.gridViewMosaic}
          style={{
            gridTemplateColumns: `repeat(${grid.cols}, max-content)`,
            gridTemplateRows: `repeat(${grid.rows}, max-content)`,
          }}
        >
          {Array.from({ length: grid.rows * grid.cols }, (_, index) => {
            const row = Math.floor(index / grid.cols);
            const col = index % grid.cols;
            const tile = tileMap.get(`${row}-${col}`);
            const src = tile?.url || null;
            if (!src) {
              return <div key={`${row}-${col}`} className={style.gridViewTileEmpty} />;
            }
            return (
              <img
                key={`${row}-${col}`}
                className={style.gridViewTile}
                src={src}
                alt={`Grid tile r${row + 1} c${col + 1}`}
                draggable={false}
              />
            );
          })}
        </div>
        <svg
          className={style.gridMaskOverlay}
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
        >
          {masks.map((mask) => (
            <polygon
              key={mask.id}
              points={pointsToSvgPolygon(mask.points)}
              className={`${style.gridMaskPolygon} ${selectedMaskId === mask.id ? style.gridMaskPolygonSelected : ''}`}
              onMouseDown={stopMaskMouseDown}
              onClick={(event) => selectMask(event, mask.id)}
              onDoubleClick={(event) => handleMaskDoubleClick(event, mask.id)}
            />
          ))}
          {draftPreview.length > 1 && (
            <polyline
              points={pointsToSvgPolygon(draftPreview)}
              className={draftLineClass}
            />
          )}
          {draftPoints.length >= 2 && cursorPoint && (
            <line
              x1={cursorPoint.x}
              y1={cursorPoint.y}
              x2={draftPoints[0].x}
              y2={draftPoints[0].y}
              className={draftLineClass}
            />
          )}
          {draftPoints.map((point) => (
            <ellipse
              key={`draft-${point.x}-${point.y}`}
              cx={point.x}
              cy={point.y}
              rx={pointRx}
              ry={pointRy}
              className={style.gridMaskDraftPoint}
            />
          ))}
        </svg>
      </div>
    );
  };

  return (
    // Pan/zoom canvas — mouse-driven; keyboard delete handled globally.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={`${style.gridViewContainer} ${mode === MODE_MASK ? style.gridViewMaskMode : ''}`}
      onMouseDown={handleMouseDown}
    >
      <div className={style.gridMaskToolbar}>
        {canMask && (
          <>
            <RoundButton
              className={`${style.gridMaskIconBtn} ${mode === MODE_MASK ? style.gridMaskIconBtnActive : ''}`}
              onClick={startMaskMode}
              title={maskTitle}
            >
              <FontAwesomeIcon size="lg" icon={faDrawPolygon} className="align-middle" />
            </RoundButton>
            <RoundButton
              className={style.gridMaskIconBtn}
              onClick={deleteSelectedMask}
              disabled={!selectedMaskId}
              title="Delete selected mask"
            >
              <FontAwesomeIcon size="lg" icon={faTrash} className="align-middle" />
            </RoundButton>
            <RoundButton
              className={style.gridMaskIconBtn}
              onClick={saveMasks}
              disabled={!isDirty || saving}
              title={saving ? 'Saving…' : 'Save masks'}
            >
              <FontAwesomeIcon size="lg" icon={faFloppyDisk} className="align-middle" />
            </RoundButton>
          </>
        )}
        <RoundButton
          className={style.gridMaskIconBtn}
          onClick={exitGridView}
          title="Exit grid view"
        >
          <FontAwesomeIcon size="lg" icon={faXmark} className="align-middle" />
        </RoundButton>
      </div>
      <div
        className={style.gridViewContent}
        style={{
          transform: `translate(calc(-50% + ${view.x}px), calc(-50% + ${view.y}px)) scale(${view.scale})`,
        }}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default GridImageViewer;
